import { SearchInput } from '@/components/ui/search-input';
import { Button, HStack } from '@chakra-ui/react';

import type { DashboardTableSortState, InvitationTableSortBy } from '@fluenceur/types';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterGroup } from '@/components/filters/filter-button';
import { FilterPanel } from '../ui';
import { InvitationStatusFilter } from './InvitationStatusFilter';
import { Xmark } from 'iconoir-react';

const INVITATION_DEFAULT_SORT = {
  sortBy: 'createdAt',
  sortDirection: 'desc',
} as const satisfies DashboardTableSortState<InvitationTableSortBy>;
type InvitationStatusFilterValue =
  | 'pending'
  | 'clicked'
  | 'joined'
  | 'expired'
  | 'undeliverable'
  | 'all';

const InvitationFilters: React.FC = () => {
  const { t } = useTranslation();

  // URL state management with nuqs - using namespaced parameters
  const [search, setSearch] = useQueryState('invitationSearch', parseAsString.withDefault(''));
  const [status, setStatus] = useQueryState('invitationStatus', parseAsString.withDefault('all'));
  const [sortBy, setSortBy] = useQueryState(
    'invitationSortBy',
    parseAsString.withDefault(INVITATION_DEFAULT_SORT.sortBy)
  );
  const [sortDirection, setSortDirection] = useQueryState(
    'invitationSortDirection',
    parseAsString.withDefault(INVITATION_DEFAULT_SORT.sortDirection)
  );
  const [, setPage] = useQueryState('invitationPage', parseAsInteger.withDefault(1));

  const handleSearchChange = useCallback(
    (newSearch: string | undefined) => {
      setSearch(newSearch || '');
      setPage(1);
    },
    [setSearch, setPage]
  );

  const handleStatusChange = useCallback(
    (newStatus: InvitationStatusFilterValue | undefined) => {
      setStatus(newStatus || 'all');
      setPage(1);
    },
    [setStatus, setPage]
  );

  // Clear all filters functionality
  const handleClearAll = useCallback(() => {
    setSearch('');
    setStatus('all');
    setSortBy(INVITATION_DEFAULT_SORT.sortBy);
    setSortDirection(INVITATION_DEFAULT_SORT.sortDirection);
    setPage(1);
  }, [setSearch, setStatus, setSortBy, setSortDirection, setPage]);

  // Check if any filters are active (different from defaults)
  const hasActiveFilters = useMemo(() => {
    return (
      search !== '' ||
      status !== 'all' ||
      sortBy !== INVITATION_DEFAULT_SORT.sortBy ||
      sortDirection !== INVITATION_DEFAULT_SORT.sortDirection
    );
  }, [search, status, sortBy, sortDirection]);

  return (
    <FilterPanel
      w="full"
      actionsSlot={
        <FilterGroup>
          <HStack gap={2} flexShrink={0}>
            <InvitationStatusFilter
              value={status as InvitationStatusFilterValue}
              onChange={handleStatusChange}
            />
            {hasActiveFilters && (
              <Button size="sm" variant="ghost" colorPalette="gray" onClick={handleClearAll}>
                <Xmark width={16} height={16} />
                {t('invitations.filters.clearAll')}
              </Button>
            )}
          </HStack>
        </FilterGroup>
      }
    >
      {/* Search Filter */}
      <SearchInput
        value={search}
        onChange={handleSearchChange}
        placeholder={t('invitations.filters.searchPlaceholder')}
        maxWidth="300px"
        size="sm"
      />
    </FilterPanel>
  );
};

export default InvitationFilters;
